<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[9998] bg-slate-950/70 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 overflow-y-auto"
    @click.self="$emit('close')"
  >
    <div
      class="bg-white dark:bg-[#111C30] w-full max-w-5xl max-h-[92vh] rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-[#263449] relative animate-fade-in flex flex-col shadow-2xl overflow-hidden"
    >
      <!-- Modal Loading Overlay -->
      <AppLoader
        :isVisible="isLoading"
        variant="card"
        size="md"
        message="Loading Student Details..."
        subtext="Fetching enrollment, contacts, and performance metrics"
        color="indigo"
      />

      <!-- Top Subtle Gradient Accent Line -->
      <div class="h-1 bg-gradient-to-r from-indigo-600 via-blue-600 to-sky-500 shrink-0"></div>

      <!-- Professional Modal Header Bar -->
      <div
        class="bg-white dark:bg-[#111C30] px-6 py-4 border-b border-slate-100 dark:border-[#263449] flex items-center justify-between shrink-0"
      >
        <div class="flex items-center gap-3">
          <div>
            <div class="flex items-center gap-2.5 flex-wrap">
              <h2 class="text-base sm:text-lg font-bold text-slate-900 dark:text-white tracking-tight">
                Student Profile
              </h2>
              <span
                v-if="student?.rollNumber"
                class="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-mono font-semibold bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-slate-300 border border-slate-200/60 dark:border-slate-700/50"
              >
                Roll: {{ student.rollNumber }}
              </span>
              <span
                class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold capitalize border"
                :class="
                  student?.status === 'active'
                    ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200/60 dark:border-emerald-500/30'
                    : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700'
                "
              >
                <span
                  class="w-1.5 h-1.5 rounded-full"
                  :class="student?.status === 'active' ? 'bg-emerald-500' : 'bg-slate-400'"
                ></span>
                {{ student?.status || 'Active' }}
              </span>
            </div>
            <p class="text-xs text-slate-400 dark:text-slate-500 mt-0.5 font-normal">
              {{ typeof student?.class === 'object' ? student?.class?.name : (student?.class || 'Enrolled') }} • {{ student?.email || 'Student Record' }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <button
            @click="$emit('edit', student)"
            class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 bg-slate-50 dark:bg-[#18243A] hover:bg-slate-100 dark:hover:bg-[#1f2d47] border border-slate-200/80 dark:border-[#263449] rounded-xl transition-all"
          >
            <Pencil class="w-3.5 h-3.5" />
            <span>Edit Profile</span>
          </button>
          <button
            @click="$emit('close')"
            :disabled="isLoading"
            class="w-8 h-8 rounded-xl flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#18243A] transition-colors disabled:opacity-50"
            title="Close Modal"
          >
            <X class="w-4 h-4" />
          </button>
        </div>
      </div>

      <!-- Scrollable Modal Content -->
      <div
        v-if="student"
        class="overflow-y-auto p-5 sm:p-7 space-y-6 flex-1 bg-slate-50/50 dark:bg-[#0B1120]"
      >
        <!-- Hero Identity Card -->
        <div
          class="relative bg-white dark:bg-[#111C30] rounded-2xl p-5 sm:p-6 border border-slate-200/80 dark:border-[#263449] shadow-xs flex flex-col sm:flex-row items-center sm:items-start gap-5 sm:gap-6"
        >
          <!-- Avatar with status indicator -->
          <div class="relative shrink-0">
            <div class="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden ring-4 ring-slate-100 dark:ring-slate-800 shadow-md">
              <img
                v-if="student.photo"
                :src="student.photo"
                :alt="student.name"
                class="w-full h-full object-cover"
              />
              <div
                v-else
                class="w-full h-full bg-gradient-to-br from-indigo-500 via-indigo-600 to-violet-600 text-white flex items-center justify-center text-2xl font-bold tracking-wider"
              >
                {{ getInitials(student.name || "") }}
              </div>
            </div>
            <span
              class="absolute -bottom-1 -right-1 w-4 h-4 rounded-full ring-2 ring-white dark:ring-[#111C30]"
              :class="
                student.status === 'active'
                  ? 'bg-emerald-500'
                  : student.status === 'passout'
                  ? 'bg-blue-500'
                  : 'bg-rose-500'
              "
              :title="student.status"
            ></span>
          </div>

          <!-- Hero Details -->
          <div class="flex-1 text-center sm:text-left min-w-0 w-full">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3 class="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
                  {{ student.name || "Student Name" }}
                </h3>
                <div class="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-2">
                  <!-- Class badge -->
                  <span
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-900/60 shadow-2xs"
                  >
                    <GraduationCap class="w-3.5 h-3.5 text-indigo-500" />
                    <span>
                      Class:
                      {{
                        typeof student.class === "object"
                          ? student.class?.name
                          : student.class || "N/A"
                      }}
                    </span>
                  </span>

                  <!-- Status capsule -->
                  <span
                    v-if="student.status === 'active'"
                    class="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900/60 shadow-2xs"
                  >
                    <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    Active Student
                  </span>
                  <span
                    v-else-if="student.status === 'passout'"
                    class="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-900/60 shadow-2xs"
                  >
                    <span class="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                    Graduated
                  </span>
                  <span
                    v-else
                    class="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs font-semibold bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-900/60 shadow-2xs"
                  >
                    <span class="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                    Inactive
                  </span>
                </div>
              </div>

              <!-- Quick Action / Edit -->
              <div class="mt-2 sm:mt-0 flex items-center justify-center sm:justify-end gap-2">
                <button
                  @click="$emit('edit', student)"
                  class="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow-xs transition"
                >
                  <Pencil class="w-3.5 h-3.5" />
                  <span>Edit Profile</span>
                </button>
              </div>
            </div>

            <!-- Attendance & Card ID Bar -->
            <div class="mt-3.5 pt-3 border-t border-slate-100 dark:border-[#1E293B] flex items-center justify-center sm:justify-start gap-4 text-xs text-slate-500 dark:text-slate-400 font-mono">
              <span class="flex items-center gap-1.5">
                <IdCard class="w-3.5 h-3.5 text-slate-400" />
                <span>Attendance ID:</span>
                <strong class="text-slate-700 dark:text-slate-200">{{ student.attendanceID || "—" }}</strong>
              </span>
              <span v-if="student.enrollmentDate" class="flex items-center gap-1.5">
                <Calendar class="w-3.5 h-3.5 text-slate-400" />
                <span>Admitted:</span>
                <strong class="text-slate-700 dark:text-slate-200">{{ formatDate(student.enrollmentDate) }}</strong>
              </span>
            </div>
          </div>
        </div>

        <!-- 2-Column Info Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
          <!-- Card 1: Personal & Contact Information -->
          <div class="bg-white dark:bg-[#111C30] p-5 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-4">
            <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
              <User class="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              <span>Contact & Personal Info</span>
            </div>

            <div class="space-y-3 text-xs">
              <!-- Email -->
              <div class="flex items-start gap-3">
                <Mail class="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Email Address</p>
                  <p class="text-slate-800 dark:text-slate-200 font-medium truncate mt-0.5">{{ student.email || "—" }}</p>
                </div>
              </div>

              <!-- Primary Phone -->
              <div class="flex items-start gap-3">
                <Phone class="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Primary Phone</p>
                  <p class="text-slate-800 dark:text-slate-200 font-mono font-medium mt-0.5">{{ student.phone || "—" }}</p>
                </div>
              </div>

              <!-- Secondary Phone -->
              <div class="flex items-start gap-3">
                <Phone class="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Guardian Phone</p>
                  <p class="text-slate-800 dark:text-slate-200 font-mono font-medium mt-0.5">{{ student.secondaryPhone || "—" }}</p>
                </div>
              </div>

              <!-- DOB -->
              <div class="flex items-start gap-3">
                <Calendar class="w-4 h-4 text-purple-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Date of Birth</p>
                  <p class="text-slate-800 dark:text-slate-200 font-medium mt-0.5">
                    {{ student.dob ? formatDate(student.dob) : "—" }}
                    <span v-if="student.dob" class="text-indigo-600 dark:text-indigo-400 font-bold ml-1.5">
                      ({{ calculateAge(student.dob) }} yrs)
                    </span>
                  </p>
                </div>
              </div>

              <!-- Address Details -->
              <div class="flex items-start gap-3">
                <MapPin class="w-4 h-4 text-rose-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Location & Address</p>
                  <p class="text-slate-800 dark:text-slate-200 font-medium mt-0.5">
                    {{ [student.city, student.subDistrict, student.state].filter(Boolean).join(", ") || "—" }}
                    <span v-if="student.pincode" class="font-mono text-slate-500"> ({{ student.pincode }})</span>
                  </p>
                  <p v-if="student.address" class="text-slate-500 dark:text-slate-400 mt-1 italic text-[11px]">
                    {{ student.address }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Card 2: Academic & Admission Record -->
          <div class="bg-white dark:bg-[#111C30] p-5 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-4">
            <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
              <GraduationCap class="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              <span>Academic & Registry Information</span>
            </div>

            <div class="space-y-3 text-xs">
              <!-- Roll Number -->
              <div class="flex items-start gap-3">
                <Hash class="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Roll Number</p>
                  <p class="text-slate-800 dark:text-slate-200 font-mono font-bold mt-0.5">{{ student.rollNumber || "—" }}</p>
                </div>
              </div>

              <!-- Class -->
              <div class="flex items-start gap-3">
                <BookOpen class="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Class / Section</p>
                  <p class="text-slate-800 dark:text-slate-200 font-medium mt-0.5">
                    {{ typeof student.class === "object" ? student.class?.name : student.class || "—" }}
                  </p>
                </div>
              </div>

              <!-- Enrollment Date -->
              <div class="flex items-start gap-3">
                <Calendar class="w-4 h-4 text-cyan-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Admission Date</p>
                  <p class="text-slate-800 dark:text-slate-200 font-medium mt-0.5">
                    {{ student.enrollmentDate ? formatDate(student.enrollmentDate) : "—" }}
                  </p>
                </div>
              </div>

              <!-- Passout Details if any -->
              <div v-if="student.status === 'passout'" class="flex items-start gap-3">
                <CheckCircle class="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-wider">Passout Details</p>
                  <p class="text-slate-800 dark:text-slate-200 font-medium mt-0.5">
                    Year: {{ student.passoutYear || "—" }} • Session: {{ student.session || "—" }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Documents Section -->
        <div class="bg-white dark:bg-[#111C30] p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-4">
          <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
            <FileText class="w-4 h-4 text-violet-600 dark:text-violet-400" />
            <span>Uploaded Identity & Academic Documents</span>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <!-- Document Card Reusable -->
            <div
              v-for="docItem in documentList"
              :key="docItem.key"
              class="p-4 rounded-xl border border-slate-200/80 dark:border-[#263449] bg-slate-50/50 dark:bg-[#0F172A] flex flex-col justify-between space-y-3"
            >
              <div>
                <div class="flex items-center justify-between mb-2">
                  <span class="text-xs font-bold text-slate-800 dark:text-white">
                    {{ docItem.label }}
                  </span>
                  <span
                    v-if="student[docItem.key]"
                    class="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded-full"
                  >
                    <CheckCircle class="w-3 h-3" /> Available
                  </span>
                  <span
                    v-else
                    class="text-[10px] text-slate-400 font-medium"
                  >
                    Not Uploaded
                  </span>
                </div>
              </div>

              <div v-if="student[docItem.key]" class="flex items-center gap-2 pt-2 border-t border-slate-200/60 dark:border-[#1E293B]">
                <button
                  type="button"
                  @click="$emit('view-document', student[docItem.key], docItem.label)"
                  class="flex-1 inline-flex items-center justify-center gap-1 px-2.5 py-1.5 bg-indigo-50 dark:bg-indigo-950/60 hover:bg-indigo-100 text-indigo-700 dark:text-indigo-300 rounded-lg text-xs font-semibold border border-indigo-200 dark:border-indigo-800 transition"
                >
                  <Eye class="w-3 h-3" />
                  <span>View</span>
                </button>
                <button
                  type="button"
                  @click="$emit('download-document', student[docItem.key], docItem.key)"
                  class="flex-1 inline-flex items-center justify-center gap-1 px-2.5 py-1.5 bg-emerald-50 dark:bg-emerald-950/60 hover:bg-emerald-100 text-emerald-700 dark:text-emerald-300 rounded-lg text-xs font-semibold border border-emerald-200 dark:border-emerald-800 transition"
                >
                  <Download class="w-3 h-3" />
                  <span>Get</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Attendance Analytics Section -->
        <div v-if="student?._id" class="pt-2">
          <StudentAttendanceAnalytics
            :selectedYear="selectedYear"
            :attendanceData="studentAttendanceData"
            @update:selectedYear="handleYearChange"
            @filter-change="handleFilterChange"
          />
        </div>
      </div>

      <!-- Footer -->
      <div
        class="p-4 sm:p-5 border-t border-slate-200/80 dark:border-[#263449] bg-white dark:bg-[#111C30] flex items-center justify-end gap-2.5 shrink-0"
      >
        <button
          @click="$emit('close')"
          class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
        >
          Close
        </button>
        <button
          @click="$emit('edit', student)"
          class="inline-flex items-center justify-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow-sm transition"
        >
          <Pencil class="w-3.5 h-3.5" />
          <span>Edit Student</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, watch } from "vue";
  import { useStore } from "vuex";
  import {
    X,
    User,
    GraduationCap,
    FileText,
    IdCard,
    CheckCircle,
    Eye,
    Download,
    Pencil,
    Mail,
    Phone,
    Calendar,
    MapPin,
    Hash,
    BookOpen,
  } from "lucide-vue-next";
  import StudentAttendanceAnalytics from "@/components/Students/AttendanceGraph.vue";

  const props = defineProps({
    show: {
      type: Boolean,
      default: false,
    },
    student: {
      type: Object,
      default: () => ({}),
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  });

  defineEmits(["close", "edit", "view-document", "download-document"]);

  const store = useStore();

  const selectedYear = ref(new Date().getFullYear());
  const studentAttendanceData = ref(null);

  const documentList = [
    { key: "aadhaarCard", label: "Aadhaar Card" },
    { key: "birthCertificate", label: "Birth Certificate" },
    { key: "transferCertificate", label: "Transfer Certificate" },
    { key: "marksheet", label: "Marksheet" },
  ];

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const getInitials = (name) =>
    name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "ST";

  const calculateAge = (dob) => {
    if (!dob) return "N/A";
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  watch(
    () => props.student?._id,
    async (newId) => {
      if (!newId) {
        studentAttendanceData.value = null;
        return;
      }
      try {
        const data = await store.dispatch("fetchStudentYearlyAttendance", {
          userId: newId,
          year: selectedYear.value,
        });
        studentAttendanceData.value = data;
      } catch (err) {
        console.error("Failed to load attendance:", err);
        studentAttendanceData.value = null;
      }
    },
    { immediate: true },
  );

  const handleYearChange = async (year) => {
    selectedYear.value = year;
    if (!props.student?._id) return;
    try {
      const data = await store.dispatch("fetchStudentYearlyAttendance", {
        userId: props.student._id,
        year,
      });
      studentAttendanceData.value = data;
    } catch (err) {
      console.error("Failed to load attendance:", err);
      studentAttendanceData.value = null;
    }
  };

  const handleFilterChange = async ({ year, startDate, endDate }) => {
    if (!props.student?._id) return;
    selectedYear.value = year;
    try {
      const data = await store.dispatch("fetchStudentYearlyAttendance", {
        userId: props.student._id,
        year,
        startDate,
        endDate,
      });
      studentAttendanceData.value = data;
    } catch (err) {
      console.error("Failed to load filtered attendance:", err);
      studentAttendanceData.value = null;
    }
  };

  watch(
    () => props.show,
    (visible) => {
      if (!visible) {
        studentAttendanceData.value = null;
      }
    },
  );
</script>

<style scoped>
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: scale(0.97);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  .animate-fade-in {
    animation: fade-in 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  }
</style>
